// "Add to calendar" and RSVP for The Stage, kept on the player's own device: an .ics file the browser saves (no
// network, no email), and an "I'm going" mark in this browser. Rooms has no RSVP yet; when it does, the mark moves to
// the account and the reminder comes through the existing notifications (event_starting). Pure apart from the
// storage and download helpers at the end.

import type { Item } from "./programme.ts";
import { VENUES } from "./programme.ts";

const SITE = "https://banger.sh";

/** 20261031T200000Z */
function stamp(ms: number): string {
  return new Date(ms).toISOString().replace(/[-:]/g, "").replace(/\.\d{3}/, "");
}

/** RFC 5545 text: backslash, semicolon, comma and newlines escaped. */
function esc(s: string): string {
  return s.replace(/\\/g, "\\\\").replace(/;/g, "\\;").replace(/,/g, "\\,").replace(/\r?\n/g, "\\n");
}

/** Lines folded at 75 octets (continuations start with a space). */
function fold(line: string): string {
  const bytes = new TextEncoder();
  const out: string[] = [];
  let cur = "";
  for (const ch of line) {
    if (bytes.encode(cur + ch).length > (out.length ? 74 : 75)) {
      out.push(cur);
      cur = "";
    }
    cur += ch;
  }
  out.push(cur);
  return out.join("\r\n ");
}

/** Where it happens, as a calendar shows it. */
export function locationText(i: Pick<Item, "venue">): string {
  return i.venue ? `${VENUES[i.venue].name[0]!.toUpperCase()}${VENUES[i.venue].name.slice(1)}, the Plaza on Banger` : "Banger";
}

/** One VEVENT calendar file, with a reminder 30 minutes before (a deadline: 2 hours). */
export function icsFor(i: Pick<Item, "id" | "kind" | "title" | "line" | "at" | "until" | "venue">, now: number): string {
  const end = i.kind === "deadline" ? i.at : Math.max(i.until, i.at + 30 * 60_000);
  const lines = [
    "BEGIN:VCALENDAR",
    "VERSION:2.0",
    "PRODID:-//Banger//The Stage//EN",
    "CALSCALE:GREGORIAN",
    "METHOD:PUBLISH",
    "BEGIN:VEVENT",
    `UID:${i.id}@stage.banger.sh`,
    `DTSTAMP:${stamp(now)}`,
    `DTSTART:${stamp(i.kind === "deadline" ? i.at - 30 * 60_000 : i.at)}`,
    `DTEND:${stamp(end)}`,
    `SUMMARY:${esc(i.title)}`,
    `DESCRIPTION:${esc(`${i.line}\n\nJoin from the Banger app. Details: ${SITE}/events`)}`,
    `LOCATION:${esc(locationText(i))}`,
    `URL:${SITE}/events`,
    "BEGIN:VALARM",
    "ACTION:DISPLAY",
    `DESCRIPTION:${esc(i.title)}`,
    `TRIGGER:-PT${i.kind === "deadline" ? "2H" : "30M"}`,
    "END:VALARM",
    "END:VEVENT",
    "END:VCALENDAR",
  ];
  return lines.map(fold).join("\r\n") + "\r\n";
}

/** "halloween-night-in-the-plaza.ics" */
export function icsName(title: string): string {
  return `${title.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "").slice(0, 60) || "event"}.ics`;
}

// ---- on this device ---------------------------------------------------------------------------------------------

const KEY = "stage:going";

/** The ids this browser said "I'm going" to (empty when storage is blocked). */
export function goingIds(store: Pick<Storage, "getItem"> | null = safeStorage()): Set<string> {
  try {
    const v = JSON.parse(store?.getItem(KEY) ?? "[]");
    return new Set(Array.isArray(v) ? v.filter((x): x is string => typeof x === "string").slice(0, 100) : []);
  } catch {
    return new Set();
  }
}

/** Flip the mark for `id`; returns whether the player is now going. Storage failures keep the page working. */
export function toggleGoing(id: string, store: Pick<Storage, "getItem" | "setItem"> | null = safeStorage()): boolean {
  const ids = goingIds(store);
  const on = !ids.has(id);
  if (on) ids.add(id);
  else ids.delete(id);
  try {
    store?.setItem(KEY, JSON.stringify([...ids]));
  } catch {
    /* private mode: the mark lasts for this page only */
  }
  return on;
}

/** Set the mark for `id` (after rooms answered). */
export function setGoing(id: string, on: boolean, store: Pick<Storage, "getItem" | "setItem"> | null = safeStorage()): void {
  if (goingIds(store).has(id) !== on) toggleGoing(id, store);
}

/**
 * Device marks that never reached rooms (review 2026-10-19): someone tapped "I'm going" signed out, then signed in.
 * True when this item's RSVP should be sent to rooms now: signed in, a night rooms holds, marked going on this
 * device, and not already sent this page view (`sent` holds the rooms event ids already sent).
 */
export function needsRsvpSync(i: { id: string; eventId?: string }, signedIn: boolean, going: ReadonlySet<string>, sent: ReadonlySet<string>): boolean {
  return signedIn && !!i.eventId && going.has(i.id) && !sent.has(i.eventId);
}

function safeStorage(): Storage | null {
  try {
    return globalThis.localStorage ?? null;
  } catch {
    return null;
  }
}

/** Save the .ics through the browser (a local Blob: nothing is uploaded). */
export function downloadIcs(i: Parameters<typeof icsFor>[0], now = Date.now()): void {
  const url = URL.createObjectURL(new Blob([icsFor(i, now)], { type: "text/calendar;charset=utf-8" }));
  const a = document.createElement("a");
  a.href = url;
  a.download = icsName(i.title);
  document.body.append(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
